import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { apiService } from '../../services/api';
import SignaturePad from './SignaturePad';

export interface AgreementContext {
  bidId?: string;
  existingCustomerId?: string;
  agreementId?: string;
}

export interface PendingAgreement {
  templateId: string;
  serviceType: string;
  serviceLabel: string;
  required: boolean;
  fileName: string;
  version: number;
  viewUrl: string | null;
}

export interface PendingAgreementsResponse {
  providerId: string;
  providerName: string;
  items: PendingAgreement[];
}

interface ProviderAgreementSigningDialogProps {
  open: boolean;
  context: AgreementContext | null;
  data: PendingAgreementsResponse | null;
  /** true when every required agreement was signed; false if the customer backed out. */
  onFinish: (completed: boolean) => void;
}

const ProviderAgreementSigningDialog: React.FC<ProviderAgreementSigningDialogProps> = ({
  open,
  context,
  data,
  onFinish,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [index, setIndex] = useState(0);
  const [typedName, setTypedName] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setIndex(0);
      setTypedName('');
      setError('');
    }
  }, [open]);

  useEffect(() => {
    setSignature(null);
    setConsent(false);
    setError('');
  }, [index]);

  const items = data?.items || [];
  const current = items[index];
  const providerName = data?.providerName || 'Your provider';

  const advance = () => {
    if (index + 1 < items.length) {
      setIndex(index + 1);
    } else {
      onFinish(true);
    }
  };

  const handleSign = async () => {
    if (!current || !context) return;
    if (typedName.trim().length < 2) {
      setError('Please type your full legal name.');
      return;
    }
    if (!signature) {
      setError('Please draw your signature.');
      return;
    }
    if (!consent) {
      setError('Please agree to sign electronically.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await apiService.post('/customer-agreements/sign', {
        context,
        templateId: current.templateId,
        typedName: typedName.trim(),
        signatureDataUrl: signature,
        consent: true,
      }, { timeout: 60000 });
      if (!res.success) throw new Error(res.error || 'Failed to sign agreement');
      advance();
    } catch (err: any) {
      setError(err?.message || 'Failed to sign agreement');
    } finally {
      setSubmitting(false);
    }
  };

  if (!current) return null;

  return (
    <Dialog open={open} fullScreen={fullScreen} maxWidth="md" fullWidth onClose={submitting ? undefined : () => onFinish(false)}>
      <DialogTitle>
        {providerName} — {current.serviceLabel} Agreement
        {items.length > 1 && (
          <Typography variant="body2" color="text.secondary">
            Agreement {index + 1} of {items.length}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            color={current.required ? 'warning' : 'default'}
            label={current.required ? 'Required to continue' : 'Optional'}
          />
          <Typography variant="body2" color="text.secondary">
            Please review {providerName}&apos;s service agreement, then sign below.
          </Typography>
        </Box>

        {current.viewUrl ? (
          <>
            <Box
              component="iframe"
              title={`${current.serviceLabel} agreement`}
              src={current.viewUrl}
              sx={{ width: '100%', height: { xs: 360, sm: 440 }, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}
            />
            <Button
              size="small"
              startIcon={<OpenInNew />}
              href={current.viewUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mt: 0.5 }}
            >
              Open full agreement
            </Button>
          </>
        ) : (
          <Alert severity="warning">The agreement could not be loaded. Please close and try again.</Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <TextField
            label="Type your full legal name"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            fullWidth
            autoComplete="name"
            disabled={submitting}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" gutterBottom>
            Draw your signature
          </Typography>
          <SignaturePad key={current.templateId} onChange={setSignature} disabled={submitting} />
          <FormControlLabel
            sx={{ mt: 1, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={submitting}
                sx={{ pt: 0.5 }}
              />
            }
            label={
              <Typography variant="body2">
                I have reviewed this agreement. I agree to sign electronically and to receive it electronically, and
                I adopt the signature and name above as my legal signature.
              </Typography>
            }
          />
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
            This agreement is between you and {providerName}. LawnHub provides the signing tool only; LawnHub did
            not write or review this agreement and is not a party to it. A signed copy will be emailed to you and
            saved in your account.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={() => onFinish(false)} disabled={submitting}>
          Cancel
        </Button>
        {!current.required && (
          <Button onClick={advance} disabled={submitting}>
            Skip for now
          </Button>
        )}
        <Button variant="contained" onClick={handleSign} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : 'Sign agreement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderAgreementSigningDialog;

/**
 * Call `requestSignatures(context)` before accepting a bid / onboarding offer.
 * Resolves true right away when there is nothing to sign, true after signing,
 * or false if the customer cancels. Render `agreementDialog` somewhere in the page.
 */
export function useProviderAgreementGate() {
  const [state, setState] = useState<{ context: AgreementContext; data: PendingAgreementsResponse } | null>(null);
  const resolverRef = useRef<((completed: boolean) => void) | null>(null);

  const requestSignatures = useCallback(async (context: AgreementContext): Promise<boolean> => {
    let data: PendingAgreementsResponse | undefined;
    try {
      const res = await apiService.get<PendingAgreementsResponse>('/customer-agreements/pending', { ...context });
      data = res.success ? res.data : undefined;
    } catch (err) {
      // The accept endpoints still enforce required agreements server-side.
      console.warn('[AGREEMENTS] Could not load pending agreements:', err);
    }
    if (!data?.items?.length) return true;
    const pending = data;
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState({ context, data: pending });
    });
  }, []);

  const handleFinish = useCallback((completed: boolean) => {
    setState(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(completed);
  }, []);

  const agreementDialog = (
    <ProviderAgreementSigningDialog
      open={!!state}
      context={state?.context || null}
      data={state?.data || null}
      onFinish={handleFinish}
    />
  );

  return { requestSignatures, agreementDialog };
}
